import React, { memo, useCallback, type MouseEvent } from 'react';
import { Box, xcss } from '@atlaskit/primitives';
import { InlineCreateTrigger } from '@atlassian/jira-business-inline-create-trigger';
import { InlineCreateForm } from '@atlassian/jira-business-issue-create';
import { useIntl } from '@atlassian/jira-intl';
import { GAP_BETWEEN_CARDS } from '../../../../../common/constants';
import type { BoardColumn } from '../../../../../common/types';
import { useInlineCreateProps } from '../../../../../controllers/inline-create-old';
import { useBoardVirtualization } from '../../../../../controllers/virtualization-context-old';
// eslint-disable-next-line jira/import/no-parent-imports
import messages from '../../../../board/column/card/inline-sibling-create/messages';

const INLINE_CREATE_TRIGGER_OFFSET = 12;

type Props = {
	column: BoardColumn;
	isSiblingCreateFormOpen: boolean;
	issueId: number;
	toggleSiblingCreateForm: () => void;
};

const InlineSiblingCreate = ({
	column,
	isSiblingCreateFormOpen,
	issueId,
	toggleSiblingCreateForm,
}: Props) => {
	const { formatMessage } = useIntl();
	const { onBoardCardResize } = useBoardVirtualization();
	const inlineCreateProps = useInlineCreateProps({
		column,
		rankBeforeIssueId: issueId,
	});

	const handleTriggerClick = useCallback(() => {
		toggleSiblingCreateForm();
		onBoardCardResize();
	}, [onBoardCardResize, toggleSiblingCreateForm]);

	// prevents onBlur from being called before the click event is finished
	const handleTriggerMouseDown = useCallback((e: MouseEvent) => {
		e.preventDefault();
	}, []);

	const handleFormBlur = useCallback(() => {
		toggleSiblingCreateForm();
		onBoardCardResize();
	}, [onBoardCardResize, toggleSiblingCreateForm]);

	return isSiblingCreateFormOpen ? (
		<Box xcss={inlineFormWrapperStyles}>
			<InlineCreateForm {...inlineCreateProps} location="boardSibling" onBlur={handleFormBlur} />
		</Box>
	) : (
		<InlineCreateTrigger
			direction="row"
			onClick={handleTriggerClick}
			onMouseDown={handleTriggerMouseDown}
			label={formatMessage(messages.createSiblingIssue)}
			offset={INLINE_CREATE_TRIGGER_OFFSET}
			position={GAP_BETWEEN_CARDS / 2}
		/>
	);
};

export default memo(InlineSiblingCreate);

const inlineFormWrapperStyles = xcss({
	marginTop: 'space.050',
});
